import { onBeforeUnmount, onMounted, ref } from 'vue';
import { usePage, router } from '@inertiajs/vue3';

export default function useTheme() {
    // Default is dark mode (`:root` in design-tokens.css is dark)
    const darkMode = ref(true);
    const page = usePage();

    const toggleDarkMode = () => {
        darkMode.value = !darkMode.value;
        applyTheme();
        saveThemePreference();
    };

    const applyTheme = () => {
        // SSR-safe: only touch the DOM when running in the browser
        if (typeof document !== 'undefined') {
            // design-tokens.css: :root = dark, .light = light mode
            if (darkMode.value) {
                document.documentElement.classList.remove('light');
            } else {
                document.documentElement.classList.add('light');
            }
        }
    };

    const saveThemePreference = () => {
        // SSR-safe localStorage access
        if (typeof document !== 'undefined') {
            localStorage.setItem('theme', darkMode.value ? 'dark' : 'light');
        }

        // If user is authenticated, save to database
        if (page.props.value && page.props.value.auth && page.props.value.auth.user) {
            router.post(route('user.preferences.theme'), {
                theme: darkMode.value ? 'dark' : 'light'
            }, {
                preserveState: true,
                preserveScroll: true,
            });
        }
    };

    onMounted(() => {
        // Check for saved theme in user preferences or localStorage
        const userTheme = page.props.value?.auth?.user?.theme;
        if (userTheme === 'dark' || userTheme === 'light') {
            darkMode.value = userTheme === 'dark';
        } else if (typeof document !== 'undefined') {
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme) {
                darkMode.value = savedTheme === 'dark';
            } else {
                // Default to dark mode when nothing is saved
                darkMode.value = true;
            }
        }

        applyTheme();
    });

    return {
        darkMode,
        toggleDarkMode
    };
}

/**
 * Read-only: which theme is on RIGHT NOW?
 *
 * `useTheme()` above OWNS the state — each caller gets its own ref, and it
 * writes the class onto <html>. That is right for the toggle button and wrong
 * for everything else: a second caller would fight the first.
 *
 * This one only watches. CSS follows the `light` class on its own, but
 * anything drawn in JavaScript does not: both price graphs had
 * `theme: { mode: 'dark' }` hardcoded plus white gridlines, so in light mode
 * the axis labels, the legend and the grid were pale grey on white.
 *
 * We observe the class rather than reading localStorage because the user can
 * switch theme without reloading, and the chart has to follow immediately.
 *
 * SSR-safe: there is no document on the server, and dark is the default.
 */
export function useCurrentTheme() {
    const isLight = ref(false);

    let observer = null;

    const read = () => {
        isLight.value = document.documentElement.classList.contains('light');
    };

    onMounted(() => {
        if (typeof document === 'undefined') return;

        read();

        observer = new MutationObserver(read);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    });

    onBeforeUnmount(() => observer?.disconnect());

    return { isLight };
}
